import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Form } from '@vodafoneis/sjonvarpskjarni-js-lib';
import PlaybackStartingTitle from './PlaybackStartingTitle';
import PlaybackStartingSubtitle from './PlaybackStartingSubtitle';
import PlaybackStartingActions from './PlaybackStartingActions';
import PlaybackStartingButtons from './PlaybackStartingButtons';
import { PlaybackContext } from '../../contexts/PlaybackContext';
import { InputPin } from '../Inputs';
import { UserContext } from '../../contexts/UserContext';
import MoviePlaylistItem from '../../models/MoviePlaylistItem';
import { breakpoints } from '../../styles/breakpoints';
import { InputState } from '../InputState';
import { Button } from '../Button';
import { SettingsContext } from '../../contexts/SettingsContext';

const PinForm = styled(Form)`
	display: flex;
	margin-bottom: 14px;

	@media ${breakpoints.sm} {
		margin-bottom: 0;
		margin-right: 14px;
	}
`;

const PinInput = styled(InputPin)`
	font-weight: bold;
	text-align: center;
	background: transparent;
	min-width: 100%;
	border-radius: 0;
	caret-color: transparent;
	border: none;
	border-bottom: 1px solid transparent;

	&:focus {
		border-bottom-color: ${(props) => (props.state === InputState.ERROR ? '#c00' : '#fff')};
		background: transparent;
	}

	&::placeholder {
		color: ${(props) => (props.state === InputState.ERROR ? '#c00' : '#fff')};
	}

	&::-webkit-credentials-auto-fill-button {
		visibility: hidden;
		pointer-events: none;
		position: absolute;
		right: 0;
	}

	&::-webkit-textfield-decoration-container {
		&::-webkit-credentials-auto-fill-button {
			display: none;
		}
	}

	@media ${breakpoints.sm} {
		min-width: 290px;
	}

	@media ${breakpoints.md} {
		min-width: 315px;
	}
`;

const PlaybackStartingPinContent: React.FC = () => {
	const { t } = useTranslation();
	const { verifyPin, isLoggedIn } = useContext(UserContext);
	const { settings } = useContext(SettingsContext);
	const { playlistItem, reset, setNextPlaybackState, allowedAgeRating, setAllowedAgeRating } = useContext(PlaybackContext);
	const [pin, setPin] = useState(null);
	const [error, setError] = useState(null);
	const [pinConfirmed, setPinConfirmed] = useState(false);
	const inputRef = useRef(null);

	const promptForPin = useMemo(() => {
		if (!(playlistItem instanceof MoviePlaylistItem)) return false;

		if (!isLoggedIn) return false;

		if (!settings) return null;

		if (pinConfirmed === true) return false;

		const { ageRating, license } = playlistItem as MoviePlaylistItem;
		const price = license.price.value;

		return settings.shouldAskForPin(ageRating, price, allowedAgeRating);
	}, [allowedAgeRating, isLoggedIn, pinConfirmed, playlistItem, settings]);

	useEffect(() => {
		// Prompt for PIN is null while it's undecided.
		if (promptForPin === false) {
			setNextPlaybackState();
		}
	}, [promptForPin, setNextPlaybackState, settings]);

	useEffect(() => {
		if (pin && pin.length > 0) {
			setError(null);
		}
	}, [pin]);

	const onPinChange = useCallback((e) => setPin(e.target.value.trim()), []);

	const onConfirm = useCallback(async () => {
		try {
			const response = await verifyPin(pin);
			if (response.isSuccess()) {
				setAllowedAgeRating((playlistItem as MoviePlaylistItem).ageRating);
				setPinConfirmed(true);
			} else {
				setPinConfirmed(false);
				setError(t('PlaybackStarting.Pin.Error.IncorrectPin'));
				setPin(null);

				if (inputRef.current) {
					inputRef.current.focus();
				}
			}
		} catch (exception) {}
	}, [pin, playlistItem, setAllowedAgeRating, t, verifyPin]);

	const onCancel = useCallback(() => reset(), [reset]);

	const placeholder = useMemo(() => error || t('PlaybackStarting.Pin.Placeholder'), [error, t]);

	if (promptForPin !== true) return null;

	return (
		<PlaybackStartingActions>
			<PinForm onSubmit={onConfirm}>
				<PinInput ref={inputRef} name={'pin'} placeholder={placeholder} state={error ? InputState.ERROR : InputState.NORMAL} autoFocus value={pin || ''} onChange={onPinChange} />
			</PinForm>
			<PlaybackStartingButtons>
				<Button primary title={t('PlaybackStarting.Pin.Buttons.Confirm')} onClick={onConfirm} />
				<Button title={t('PlaybackStarting.Pin.Buttons.Cancel')} onClick={onCancel} />
			</PlaybackStartingButtons>
		</PlaybackStartingActions>
	);
};

const PlaybackStartingPin: React.FC = () => {
	const { t } = useTranslation();
	const { playlistItem } = useContext(PlaybackContext);

	return (
		<div>
			<PlaybackStartingTitle>{t('PlaybackStarting.Pin.Watch.Title')}</PlaybackStartingTitle>
			<PlaybackStartingSubtitle>{playlistItem.getContentTitle()}</PlaybackStartingSubtitle>
			<PlaybackStartingPinContent />
		</div>
	);
};

export default PlaybackStartingPin;
